import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["modal", "itemSelect", "quantityField", "priceField", "unitLabel", "template", "taskItems", "newItemForm", "newItemName", "newItemUnit", "newItemPrice", "newItemQuantity", "newItemError", "createItemButton", "externalParts", "externalPartTemplate", "externalName", "externalPartNumber", "externalOrderNumber", "externalSupplier", "externalQuantity", "externalUnitPrice", "externalUnitCost", "externalNotes"]

    open(event) {
        const task = event.currentTarget.closest("[data-worksheet-task-items-target~='task']")
        this.currentTaskItems = task?.querySelector("[data-worksheet-task-items-target~='taskItems']")
        this.currentTemplate = task?.querySelector("[data-worksheet-task-items-target~='template']")
        this.currentExternalParts = task?.querySelector("[data-worksheet-task-items-target~='externalParts']")
        this.currentExternalPartTemplate = task?.querySelector("[data-worksheet-task-items-target~='externalPartTemplate']")
        const partsSection = task?.querySelector("[data-task-parts-section]")
        if (partsSection) partsSection.open = true
        this.mode = event.currentTarget.dataset.mode || (this.hasItemSelectTarget ? "warehouse" : "external")

        if (this.mode === "warehouse" && (!this.currentTaskItems || !this.currentTemplate)) return
        if (this.mode === "external" && (!this.currentExternalParts || !this.currentExternalPartTemplate)) return

        const tab = this.modalTarget.querySelector(`[data-mode="${this.mode}"]`)
        if (tab) window.bootstrap?.Tab.getOrCreateInstance(tab).show()
        window.bootstrap?.Modal.getOrCreateInstance(this.modalTarget).show()
    }

    setMode(event) {
        this.mode = event.currentTarget.dataset.mode
    }

    limitQuantity(event) {
        const maximum = parseFloat(event.currentTarget.max)
        const quantity = parseFloat(event.currentTarget.value)
        if (Number.isFinite(maximum) && Number.isFinite(quantity) && quantity > maximum) {
            event.currentTarget.value = maximum
        }
    }

    prefill() {
        const option = this.itemSelectTarget.selectedOptions[0]
        if (!option?.value) return

        this.quantityFieldTarget.value = "1"
        this.quantityFieldTarget.min = option.dataset.wholeUnit === "true" ? "1" : "0.001"
        this.quantityFieldTarget.step = option.dataset.wholeUnit === "true" ? "1" : "0.001"
        this.quantityFieldTarget.max = option.dataset.available
        this.priceFieldTarget.value = option.dataset.price
        this.unitLabelTarget.textContent = option.dataset.unitLabel
    }

    showNewItemForm() {
        this.newItemFormTarget.classList.remove("d-none")
        this.newItemNameTarget.focus()
    }

    hideNewItemForm() {
        this.newItemFormTarget.classList.add("d-none")
        this.newItemErrorTarget.classList.add("d-none")
        this.newItemErrorTarget.textContent = ""
    }

    async createWarehouseItem() {
        if (!this.newItemNameTarget.value.trim() || !this.newItemUnitTarget.value) {
            this.showNewItemError(this.pickerMessages().missingFields)
            return
        }

        this.createItemButtonTarget.disabled = true
        const formData = new FormData()
        formData.append("warehouse_item[name]", this.newItemNameTarget.value.trim())
        formData.append("warehouse_item[unit]", this.newItemUnitTarget.value)
        formData.append("warehouse_item[sales_price]", this.newItemPriceTarget.value || "0")
        formData.append("warehouse_item[quantity_on_hand]", this.newItemQuantityTarget.value || "0")

        try {
            const response = await fetch("/warehouse_items.json", {
                method: "POST",
                headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
                body: formData
            })
            const data = await response.json()

            if (!response.ok) {
                this.showNewItemError(data.errors?.join(", ") || this.pickerMessages().createError)
                return
            }

            const option = new Option(data.name, data.id, true, true)
            option.dataset.name = data.name
            option.dataset.price = data.sales_price
            option.dataset.available = data.quantity_available
            option.dataset.wholeUnit = data.whole_unit
            option.dataset.unitLabel = data.unit_label
            this.itemSelectTarget.add(option)
            this.prefill()
            this.hideNewItemForm()
        } catch (_error) {
            this.showNewItemError(this.pickerMessages().createError)
        } finally {
            this.createItemButtonTarget.disabled = false
        }
    }

    showNewItemError(message) {
        this.newItemErrorTarget.textContent = message
        this.newItemErrorTarget.classList.remove("d-none")
    }

    pickerMessages() {
        const picker = this.itemSelectTarget.closest("[data-warehouse-item-picker]")
        return { missingFields: picker.dataset.missingFieldsMessage, createError: picker.dataset.createErrorMessage }
    }

    add() {
        if (this.mode === "external") {
            this.addExternalPart()
            return
        }

        const option = this.itemSelectTarget.selectedOptions[0]
        if (!option?.value || !this.currentTaskItems) return

        const index = Date.now()
        const html = this.currentTemplate.innerHTML.replaceAll("NEW_TASK_ITEM", index)
        this.currentTaskItems.insertAdjacentHTML("beforeend", html)

        const fields = this.currentTaskItems.lastElementChild
        fields.querySelector("[data-worksheet-task-items-field='warehouse-item-id']").value = option.value
        fields.querySelector("[data-worksheet-task-items-field='warehouse-item-name']").value = option.dataset.name
        fields.querySelector("[data-worksheet-task-items-field='quantity']").value = this.quantityFieldTarget.value
        fields.querySelector("[data-worksheet-task-items-field='quantity']").min = this.quantityFieldTarget.min
        fields.querySelector("[data-worksheet-task-items-field='quantity']").max = this.quantityFieldTarget.max
        fields.querySelector("[data-worksheet-task-items-field='quantity']").step = this.quantityFieldTarget.step
        fields.querySelector("[data-worksheet-task-items-field='unit-price']").value = this.priceFieldTarget.value

        this.notifyPartsChanged(fields)

        window.bootstrap?.Modal.getInstance(this.modalTarget)?.hide()
        this.reset()
    }

    addExternalPart() {
        if (!this.externalNameTarget.value.trim() || !this.currentExternalParts || !this.currentExternalPartTemplate) return

        const html = this.currentExternalPartTemplate.innerHTML.replaceAll("NEW_EXTERNAL_PART", Date.now())
        this.currentExternalParts.insertAdjacentHTML("beforeend", html)
        const fields = this.currentExternalParts.lastElementChild
        const values = {
            name: this.externalNameTarget.value,
            "part-number": this.externalPartNumberTarget.value,
            "order-number": this.externalOrderNumberTarget.value,
            supplier: this.externalSupplierTarget.value,
            quantity: this.externalQuantityTarget.value,
            "unit-price": this.externalUnitPriceTarget.value,
            "unit-cost": this.externalUnitCostTarget.value,
            notes: this.externalNotesTarget.value
        }
        Object.entries(values).forEach(([field, value]) => {
            fields.querySelector(`[name$='[${field.replaceAll("-", "_")}]']`).value = value
        })

        this.notifyPartsChanged(fields)

        window.bootstrap?.Modal.getInstance(this.modalTarget)?.hide()
        this.resetExternalPart()
    }

    reset() {
        this.itemSelectTarget.value = ""
        this.quantityFieldTarget.value = ""
        this.priceFieldTarget.value = ""
        this.unitLabelTarget.textContent = ""
    }

    resetExternalPart() {
        this.externalNameTarget.value = ""
        this.externalPartNumberTarget.value = ""
        this.externalOrderNumberTarget.value = ""
        this.externalSupplierTarget.value = ""
        this.externalQuantityTarget.value = "1"
        this.externalUnitPriceTarget.value = "0"
        this.externalUnitCostTarget.value = "0"
        this.externalNotesTarget.value = ""
    }

    notifyPartsChanged(fields) {
        fields.dispatchEvent(new CustomEvent("worksheet:parts-changed", { bubbles: true }))
    }
}
